import React from 'react';
import { Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Componente de Loading Overlay General
 * 
 * @param {boolean} isLoading - Controla la visibilidad del loading
 * @param {string} message - Mensaje opcional a mostrar (default: "Cargando...")
 * @param {string} variant - Variante del overlay: 'fullscreen' | 'inline' (default: 'fullscreen')
 * @param {string} size - Tamaño del spinner: 'sm' | 'md' | 'lg' (default: 'md')
 * 
 * @example
 * // Loading de pantalla completa
 * <LoadingOverlay isLoading={loading} message="Cargando datos..." />
 * 
 * @example
 * // Loading inline (dentro de un contenedor)
 * <LoadingOverlay isLoading={loading} variant="inline" size="sm" />
 */
const LoadingOverlay = ({ 
  isLoading = false, 
  message = 'Cargando...', 
  variant = 'fullscreen',
  size = 'md'
}) => {
  // Tamaños del spinner
  const spinnerSizes = {
    sm: 'h-6 w-6',
    md: 'h-10 w-10',
    lg: 'h-16 w-16'
  };

  // Estilos según variante
  const containerStyles = {
    fullscreen: 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm',
    inline: 'absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg'
  };

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={containerStyles[variant]}
        >
          <div className="flex flex-col items-center gap-4">
            {/* Spinner animado */}
            <Loader2 
              className={`${spinnerSizes[size]} text-primary animate-spin`}
            />
            
            {/* Mensaje */}
            {message && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-sm font-medium text-foreground"
              >
                {message}
              </motion.p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingOverlay;
